"use client";

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { parentScrollToTheTop } from "../..//lib/utils";
import useAutosize from "../../lib/use_autosize";
import styles from "../../styles/Form.module.css";

interface SimpleProps {
  children: JSX.Element;
  fullHeight?: boolean;
  maxWidthClass?: string;
  noAutosizing?: boolean;
  pagePath?: string; // For analytics
  widthClass?: string;
  onHide: () => void;
  show: boolean;
}

export default function Simple({
  children,
  fullHeight,
  maxWidthClass = "max-w-2xl",
  noAutosizing,
  onHide,
  pagePath,
  show = false,
  widthClass = "w-full",
}: SimpleProps) {
  const isInitialMount = useRef(true);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [setElement, element] = useAutosize({
    enabled: show && !noAutosizing,
    label: "simpledialog",
  });

  useEffect(() => {
    // Avoid triggering parent scroll on initial mount; only scroll when dialog opens.
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (show) parentScrollToTheTop();
  }, [show]);

  return (
    <>
      <div
        id="tailwind-safelist"
        className="md:max-w-md md:max-w-2xl md:max-w-4xl md:max-w-6xl md:max-w-full"
      ></div>

      <Dialog
        open={show}
        onClose={onHide}
        className="fixed inset-0 z-50 overflow-y-auto sm:overflow-hidden"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-50 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div
            // className="flex items-end justify-center min-h-full p-4 text-center sm:items-center sm:p-0"
            className="flex text-center md:block md:px-2 lg:px-4"
            ref={(el) => {
              if (!dialogRef.current && !element) setElement(el);
              dialogRef.current = el;
            }}
          >
            <DialogPanel
              transition
              className={`relative  overflow-auto flex self-center text-base text-left transition ${widthClass} md:inline-block md:${maxWidthClass} md:px-4 ${
                fullHeight ? "md:my-2" : "md:my-8"
              } md:align-middle`}
            >
              <div
                className={`autosize w-full ${
                  fullHeight
                    ? "md:overflow-auto md:h-[calc(100vh-1em)] h-[calc(100vh)] "
                    : "overflow-hidden"
                } md:rounded-lg relative flex items-top bg-white px-4 pt-14 pb-8 shadow-2xl sm:px-6 sm:pt-8 md:p-6 lg:p-8`}
              >
                <button
                  aria-label="Close"
                  type="button"
                  className={classNames(
                    styles.link,
                    "absolute top-4 right-4 text-gray-400 hover:text-gray-500 sm:top-8 sm:right-6 md:top-6 md:right-6 lg:top-8 lg:right-8 rounded-md z-50",
                  )}
                  onClick={onHide}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="w-6 h-6" aria-hidden="true" />
                </button>
                <div className="w-full">
                  {children}
                  {/* Invisible element with negative margins to fix mobile iframe scrolling */}
                  <div className="-ml-10 -mr-10 mt-0 -mb-8" style={{ height: '1px', visibility: 'hidden' }} aria-hidden="true" />
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
}
